import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../api/endpoints';
import { VEHICLE_ICON } from '../../constants';
import { fileUrl } from '../../api/client';
import PageHeader from '../../components/PageHeader';
import { Button, Chip, Empty, Loading } from '../../components/Ui';
import { Reveal, stagger } from '../../components/Motion';
import { currency, dayMonth } from '../../utils/format';
import { SERVICE_MODES } from '../../constants';

/**
 * "आपकी Bike/Scooter की सर्विस" - what a two-wheeler can be booked for, the
 * customer's own vehicles, and the jobs already running.
 */
const BikeServicePage = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [jobs, setJobs] = useState([]);
  // Same page for cars and two-wheelers; ?family=car picks the words, the price list and the vehicles.
  const [params] = useSearchParams();
  const isCar = params.get('family') === 'car';
  const family = isCar ? 'car' : 'two';
  const [kind, setKind] = useState(isCar ? 'car' : '');
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    api.garage.services({ forKind: kind || (isCar ? 'car' : undefined) })
      .then((r) => setServices(r.data || []))
      .catch(() => setServices([]))
      .finally(() => setLoading(false));
    api.garage.vehicles().then((r) => setVehicles((r.data || []).filter((v) => (v.kind === 'car') === isCar))).catch(() => setVehicles([]));
    api.garage.myJobs({ limit: 5 }).then((r) => setJobs(r.data || [])).catch(() => setJobs([]));
  }, [kind, isCar]);
  useEffect(load, [load]);

  const running = jobs.filter((j) => !['delivered', 'cancelled', 'rejected'].includes(j.status));

  return (
    <div>
      <PageHeader
        title={isCar ? 'Car service' : 'Bike & scooter service'}
        subtitle="Doorstep, pickup & drop, or at the garage"
        right={<Button title="My vehicles" variant="light" size="sm" onClick={() => navigate(`/my-vehicles-service?family=${family}`)} />}
      />

      <div className="split-3" style={{ gap: 12, marginBottom: 16 }}>
        {SERVICE_MODES.map((m) => (
          <div className="card sh-sm" key={m.value}>
            <div className="t-body" style={{ fontWeight: 700 }}>{m.label}</div>
            <div className="t-tiny">{m.hint}</div>
          </div>
        ))}
      </div>

      {running.length ? (
        <div className="card sh-sm" style={{ marginBottom: 16 }}>
          <div className="t-title" style={{ marginBottom: 8 }}>Running services</div>
          {running.map((j) => (
            <button type="button" key={j._id} className="row-between row-line" style={{ width: '100%', border: 0, background: 'none', cursor: 'pointer' }}
              onClick={() => navigate(`/service-jobs/${j._id}`)}>
              <span style={{ textAlign: 'left' }}>
                <span className="t-title" style={{ display: 'block' }}>{j.vehicleInfo && j.vehicleInfo.brand} {j.vehicleInfo && j.vehicleInfo.model}</span>
                <span className="t-tiny">{j.jobNo} · {j.status.replace(/_/g, ' ')}</span>
                {j.estimate && j.estimate.status === 'pending' ? (
                  <span className="t-tiny" style={{ color: 'var(--warning)', fontWeight: 700, display: 'block' }}>
                    Approval needed · {currency(j.estimate.total)}
                  </span>
                ) : null}
              </span>
              <span>›</span>
            </button>
          ))}
        </div>
      ) : null}

      <div className="card sh-sm" style={{ marginBottom: 16 }}>
        <div className="t-title" style={{ marginBottom: 8 }}>My vehicles</div>
        {!vehicles.length ? (
          <Button title={isCar ? '+ Add your car' : '+ Add your bike or scooter'} variant="ghost" onClick={() => navigate(`/my-vehicles-service?family=${family}&add=1`)} />
        ) : (
          <div className="row wrap" style={{ gap: 12 }}>
            {vehicles.map((v) => (
              <button type="button" key={v._id} className="card" style={{ width: 170, cursor: 'pointer', textAlign: 'left' }}
                onClick={() => navigate(`/my-vehicles-service?vehicle=${v._id}`)}>
                {v.photo ? (
                  <img src={fileUrl(v.photo)} alt={v.model} style={{ width: 44, height: 44, borderRadius: 8, objectFit: 'cover' }} />
                ) : <span style={{ fontSize: 26 }}>{VEHICLE_ICON[v.kind] || '🏍️'}</span>}
                <div className="t-small">{v.brand} {v.model}</div>
                <div className="t-tiny">{v.regNumber || `${v.currentKm} km`}</div>
                {v.nextServiceAt ? <div className="t-tiny" style={{ color: 'var(--primary)' }}>Next: {dayMonth(v.nextServiceAt)}</div> : null}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="row wrap" style={{ gap: 8, marginBottom: 14 }}>
        {isCar ? null : (
          <>
            <Chip label="All" active={!kind} onClick={() => setKind('')} />
            <Chip label="🏍️ Bike" active={kind === 'bike'} onClick={() => setKind('bike')} />
            <Chip label="🛵 Scooter" active={kind === 'scooter'} onClick={() => setKind('scooter')} />
          </>
        )}
      </div>

      {loading ? <Loading /> : !services.length ? (
        <Empty emoji="🔧" title="No services listed" text="KAAMWALA has not published the price list yet." />
      ) : (
        <div className="grid grid-cards">
          {services.map((s, i) => (
            <Reveal key={s._id} delay={stagger(i, 40)}>
              <button type="button" className="card sh-sm" style={{ width: '100%', textAlign: 'left', cursor: 'pointer' }}
                onClick={() => navigate(`/bike-service/book?service=${s._id}&family=${family}`)}>
                <div style={{ fontSize: 26 }}>{s.icon}</div>
                <div className="t-title">{s.title}</div>
                {s.description ? <div className="t-tiny">{s.description}</div> : null}
                <div className="t-small" style={{ color: 'var(--success)', fontWeight: 800, marginTop: 6 }}>
                  from {currency(s.fromPrice)}
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      )}
    </div>
  );
};

export default BikeServicePage;
